import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import '../styles/v2.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/* Striped canvas pattern — matches original Overview.js */
function createStripedPattern(color, opacity = 0.3) {
  const canvas = document.createElement('canvas');
  canvas.width = 10; canvas.height = 10;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(255,255,255,0)';
  ctx.fillRect(0, 0, 10, 10);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.globalAlpha = opacity;
  ctx.beginPath(); ctx.moveTo(0, 10);  ctx.lineTo(10, 0);  ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-5, 5);  ctx.lineTo(5, -5);  ctx.stroke();
  ctx.beginPath(); ctx.moveTo(5, 15);  ctx.lineTo(15, 5);  ctx.stroke();
  return ctx.createPattern(canvas, 'repeat');
}

function formatK(val) {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  if (n >= 1000) return `K ${(n / 1000).toFixed(1)}k`;
  return `K ${n.toFixed(0)}`;
}

function formatStock(val) {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function formatProductName(p) {
  let s = p.productName || '';
  if (p.variety) s += ` - ${p.variety}`;
  if (p.brand) s += ` (${p.brand})`;
  if (p.size) s += ` [${p.size}]`;
  return s;
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(/[\s\-()[\]]+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const GRADIENT_COLORS = [
  'linear-gradient(135deg,#f87171,#ef4444)',
  'linear-gradient(135deg,#fbbf24,#d97706)',
  'linear-gradient(135deg,#60a5fa,#3b82f6)',
  'linear-gradient(135deg,#a78bfa,#8b5cf6)',
  'linear-gradient(135deg,#34d399,#10b981)',
  'linear-gradient(135deg,#f472b6,#ec4899)',
  'linear-gradient(135deg,#fb923c,#ea580c)',
];

function getThisWeekRange() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  // Use local date parts — toISOString() converts to UTC which shifts the date in UTC+2
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: fmt(monday), to: fmt(sunday) };
}

function ChartLegend() {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 10, height: 10, borderRadius: 2, background: '#1976d2' }} />
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Actual</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ display: 'flex', gap: 1.5 }}>{[0,1,2].map(i=><div key={i} style={{ width:4, height:1, background:'#94a3b8' }} />)}</div>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>Projected</span>
      </div>
    </div>
  );
}

function NoData() {
  return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'#94a3b8', fontSize:13 }}>No data yet</div>;
}

export default function OverviewV2() {
  const { username = '', greeting = 'Good morning' } = useOutletContext() || {};
  const navigate = useNavigate();

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [overviewData, setOverviewData] = useState([]);
  const [forecastData, setForecastData] = useState(null);
  const [productsBelowThreshold, setProductsBelowThreshold] = useState([]);
  const [yesterdaySales, setYesterdaySales] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [overviewRes, forecastRes, belowRes] = await Promise.allSettled([
          axiosInstance.get('/overview'),
          axiosInstance.get('/forecasts/chart-data'),
          axiosInstance.get('/overview/below-threshold'),
        ]);

        if (overviewRes.status === 'fulfilled') {
          const { finalProfits, yesterdaySales: ys } = overviewRes.value.data;
          if (ys !== null && ys !== undefined) setYesterdaySales(ys);
          const products = finalProfits.profitsForWeek.map((item, idx) => ({
            productName: item.productName,
            brand: item.brand,
            variety: item.variety,
            size: item.size,
            productId: item.productId,
            profit: item.profit,
            profitLastWeek: finalProfits.profitsForPrevWeek[idx]?.profit || 0,
            currentStock: item.currentStock,
          }));
          setOverviewData(products);
        }

        if (forecastRes.status === 'fulfilled') {
          setForecastData(forecastRes.value.data);
        } else {
          setForecastData({ salesChart: { lastWeek: 0, thisWeek: 0, forecast: 0 }, profitChart: { lastWeek: 0, thisWeek: 0, forecast: 0 } });
        }

        if (belowRes.status === 'fulfilled') {
          setProductsBelowThreshold(belowRes.value.data.productsBelowThreshold || []);
        }
      } catch (err) {
        console.error('Overview fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const weekSales = forecastData?.salesChart?.thisWeek || 0;
  const weekProfit = forecastData?.profitChart?.thisWeek || 0;
  const productCount = overviewData.length;
  const lowStockCount = productsBelowThreshold.length;

  const criticalItems = productsBelowThreshold.filter(p => p.currentStock !== undefined && Number(p.currentStock) <= 2);
  const lowItems = productsBelowThreshold.filter(p => p.currentStock === undefined || Number(p.currentStock) > 2);

  const topProducts = [...overviewData].sort((a, b) => Number(b.profit) - Number(a.profit)).slice(0, 5);

  /* ── Chart helpers — matches original Overview.js exactly ── */
  const makeChartData = (actual, thisWeek, forecast, actualColors, borderColors, stripeColor) => ({
    labels: [actual[0], actual[1]],
    datasets: [
      {
        label: 'Actual',
        data: [actual[2], actual[3]],
        backgroundColor: actualColors,
        borderColor: borderColors,
        borderWidth: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        minBarLength: 2,
      },
      {
        label: 'Projected',
        data: [null, Math.max(0, (forecast || 0) - (thisWeek || 0))],
        backgroundColor: createStripedPattern(stripeColor, 0.5),
        borderColor: stripeColor,
        borderWidth: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    ],
  });

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, stacked: true, min: 0, ticks: { callback: v => Math.round(v), color: '#94a3b8', font: { size: 11, family: 'Inter' } }, grid: { color: '#f1f5f1' }, border: { display: false } },
      y: { stacked: true, ticks: { color: '#94a3b8', font: { size: 12, family: 'Inter' } }, grid: { display: false }, border: { display: false } },
    },
  };

  const salesChartData = forecastData ? makeChartData(
    ['Sales Last Week', 'Sales This Week', forecastData.salesChart?.lastWeek || 0, forecastData.salesChart?.thisWeek || 0],
    forecastData.salesChart?.thisWeek || 0,
    forecastData.salesChart?.forecast || 0,
    ['#90caf9', '#1976d2'], ['#1565c0', '#0d47a1'], '#1976d2'
  ) : null;

  const profitChartData = forecastData ? makeChartData(
    ['Profits Last Week', 'Profits This Week', forecastData.profitChart?.lastWeek || 0, forecastData.profitChart?.thisWeek || 0],
    forecastData.profitChart?.thisWeek || 0,
    forecastData.profitChart?.forecast || 0,
    ['#a5d6a7', '#388e3c'], ['#2e7d32', '#1b5e20'], '#388e3c'
  ) : null;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#10b981', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#64748b', fontSize: 14 }}>Loading dashboard…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Hero Banner ── */}
      <div className="v2-hero-bg" style={{ borderRadius: 20, overflow: 'hidden', position: 'relative', minHeight: 160 }}>
        <div style={{ position: 'absolute', bottom: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle,rgba(16,185,129,0.12) 0%,transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px,transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />

        <div className="v2-hero-inner" style={{ position: 'relative', zIndex: 1, padding: '28px 28px', display: 'flex', alignItems: 'center', gap: 24 }}>
          {/* Left: greeting + KPIs */}
          <div style={{ flex: 1 }}>
            <p style={{ color: 'rgba(52,211,153,0.65)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <h2 style={{ color: 'white', fontSize: 26, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 6 }}>
              {greeting}, {username || 'there'} 👋
            </h2>
            <p style={{ color: '#94a3b8', fontSize: 14 }}>
              {yesterdaySales !== null ? (
                <>Yesterday's sales: <span style={{ color: '#34d399', fontWeight: 700 }}>K {Number(yesterdaySales).toFixed(2)}</span></>
              ) : 'Your shop dashboard is live.'}
            </p>
            <div className="v2-hero-kpis" style={{ display: 'flex', alignItems: 'center', gap: 28, marginTop: 20 }}>
              {[
                { label: 'Weekly Sales', value: formatK(weekSales) },
                { label: 'Weekly Profit', value: formatK(weekProfit) },
                { label: 'Products', value: String(productCount) },
              ].map((kpi, i) => (
                <React.Fragment key={kpi.label}>
                  {i > 0 && <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.1)' }} />}
                  <div>
                    <p style={{ color: '#475569', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{kpi.label}</p>
                    <p style={{ color: 'white', fontSize: 22, fontWeight: 900, letterSpacing: '-0.03em', marginTop: 2 }}>{kpi.value}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right: quick actions */}
          <div className="v2-hero-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="v2-btn-hero-primary" onClick={() => navigate('/new/dashboard/sales/add')}>
              <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Sale
            </button>
            <button className="v2-btn-hero-secondary" onClick={() => navigate('/new/dashboard/purchases/add')}>
              <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
              Add Purchase
            </button>
            <button className="v2-btn-hero-ghost" onClick={() => navigate('/new/dashboard/transactions')}>
              <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
              </svg>
              Transaction
            </button>
          </div>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="v2-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>

        {/* Weekly Sales — clickable → View Sales filtered to this week */}
        <div className="v2-stat-card v2-green" onClick={() => { const { from, to } = getThisWeekRange(); navigate(`/new/dashboard/sales/view?from=${from}&to=${to}`); }}
          style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(16,185,129,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="v2-icon-green" style={{ width: 48, height: 48, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
              </svg>
            </div>
            <span className="v2-badge" style={{ background: '#f0fdf4', color: '#15803d' }}>This week</span>
          </div>
          <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Weekly Sales</p>
          <p style={{ color: '#0f172a', fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>{formatK(weekSales)}</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginTop: 16, height: 28 }}>
            {[45, 65, 50, 80, 60, 100, 32].map((h, i) => (
              <div key={i} style={{ flex: 1, borderRadius: 2, background: i === 5 ? '#10b981' : (i === 3 ? '#4ade80' : '#bbf7d0'), height: `${h}%` }} />
            ))}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 11, fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
            View all sales
            <svg style={{ width: 11, height: 11 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
          </p>
        </div>

        {/* Products — clickable */}
        <div className="v2-stat-card v2-blue" onClick={() => navigate('/new/dashboard/products/view')}
          style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(59,130,246,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="v2-icon-blue" style={{ width: 48, height: 48, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
              </svg>
            </div>
            <span className="v2-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Active</span>
          </div>
          <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Products</p>
          <p style={{ color: '#0f172a', fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>{productCount}</p>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: '#94a3b8', fontSize: 10 }}>Tracked products</span>
              <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600 }}>{productCount}</span>
            </div>
            <div style={{ height: 6, borderRadius: 99, background: '#dbeafe', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: '96%', borderRadius: 99, background: 'linear-gradient(90deg,#60a5fa,#3b82f6)' }} />
            </div>
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 11, fontWeight: 600, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 4 }}>
            View all products
            <svg style={{ width: 11, height: 11 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
          </p>
        </div>

        {/* Low Stock — clickable when there are issues */}
        <div className="v2-stat-card v2-amber"
          onClick={() => lowStockCount > 0 && navigate('/new/dashboard/reorder')}
          style={{ cursor: lowStockCount > 0 ? 'pointer' : 'default', transition: 'transform 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { if (lowStockCount > 0) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(245,158,11,0.15)'; }}}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="v2-icon-amber" style={{ width: 48, height: 48, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            {lowStockCount > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 99, background: '#fef3c7' }}>
                <div className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
                <span style={{ color: '#92400e', fontSize: 10, fontWeight: 700 }}>Urgent</span>
              </div>
            ) : (
              <span className="v2-badge" style={{ background: '#f0fdf4', color: '#15803d' }}>OK</span>
            )}
          </div>
          <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Low Stock</p>
          <p style={{ color: '#0f172a', fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>
            {lowStockCount} <span style={{ fontSize: 16, fontWeight: 500, color: '#94a3b8' }}>items</span>
          </p>
          <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 16 }}>
            {criticalItems.length > 0 ? `${criticalItems.length} critical · ` : ''}{lowItems.length} low{lowStockCount === 0 ? ' · all good!' : ' · needs reorder'}
          </p>
          {lowStockCount > 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 11, fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 4 }}>
              View reorder list
              <svg style={{ width: 11, height: 11 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </p>
          )}
        </div>

        {/* Weekly Profit — clickable → Insights */}
        <div className="v2-stat-card v2-violet" onClick={() => navigate('/new/dashboard/insights')}
          style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(124,58,237,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="v2-icon-violet" style={{ width: 48, height: 48, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg style={{ width: 24, height: 24 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
              </svg>
            </div>
            <span className="v2-badge" style={{ background: '#f5f3ff', color: '#7c3aed' }}>Est. 30%</span>
          </div>
          <p style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Weekly Profit</p>
          <p style={{ color: '#0f172a', fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>{formatK(weekProfit)}</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginTop: 16, height: 28 }}>
            {[50, 68, 55, 78, 62, 100, 35].map((h, i) => (
              <div key={i} style={{ flex: 1, borderRadius: 2, background: i === 5 ? '#9333ea' : (i === 3 ? '#c4b5fd' : '#ede9fe'), height: `${h}%` }} />
            ))}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 11, fontWeight: 600, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 4 }}>
            View insights
            <svg style={{ width: 11, height: 11 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
          </p>
        </div>
      </div>

      {/* ── Chart + Stock Alerts ── */}
      <div className="v2-chart-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>

        {/* Two stacked charts — Sales + Profits */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          {/* Weekly Sales */}
          <div className="v2-card" style={{ padding: 20, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ color: '#1e293b', fontWeight: 700, fontSize: 14, margin: 0 }}>Weekly Sales</h3>
              <ChartLegend />
            </div>
            <div style={{ height: 200, position: 'relative', overflow: 'hidden', width: '100%' }}>
              {salesChartData
                ? <Bar data={salesChartData} options={chartOptions} />
                : <NoData />}
            </div>
          </div>

          {/* Weekly Profits */}
          <div className="v2-card" style={{ padding: 20, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ color: '#1e293b', fontWeight: 700, fontSize: 14, margin: 0 }}>Weekly Profits</h3>
              <ChartLegend />
            </div>
            <div style={{ height: 200, position: 'relative', overflow: 'hidden', width: '100%' }}>
              {profitChartData
                ? <Bar data={profitChartData} options={chartOptions} />
                : <NoData />}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#94a3b8' }}>Estimated ~30% margin</p>
          </div>
        </div>

        {/* Stock Alerts */}
        <div className="v2-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ color: '#1e293b', fontWeight: 700, fontSize: 15, margin: 0 }}>Stock Alerts</h3>
            {lowStockCount > 0 && (
              <span className="v2-badge" style={{ background: '#fef3c7', color: '#92400e' }}>{lowStockCount} items</span>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {productsBelowThreshold.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '24px 0', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ width: 22, height: 22, color: '#10b981' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center' }}>All stock levels are healthy</p>
              </div>
            ) : (() => {
                const MAX = 5;
                const criticalToShow = Math.min(criticalItems.length, MAX);
                const lowToShow      = Math.min(lowItems.length, Math.max(0, MAX - criticalToShow));
                const remaining      = (criticalItems.length + lowItems.length) - criticalToShow - lowToShow;
                return (
                  <>
                    {criticalItems.length > 0 && (
                      <>
                        <p style={{ fontSize: 10, fontWeight: 700, color: '#f87171', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 4px 4px' }}>Critical ({criticalItems.length})</p>
                        {criticalItems.slice(0, criticalToShow).map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, border: '1px solid #fef2f2', cursor: 'default' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ color: '#1e293b', fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatProductName(p)}</p>
                              {p.currentStock !== undefined && <p style={{ color: '#94a3b8', fontSize: 11 }}>{formatStock(p.currentStock)} remaining</p>}
                            </div>
                            <span style={{ color: '#ef4444', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>Reorder</span>
                          </div>
                        ))}
                      </>
                    )}
                    {lowItems.length > 0 && lowToShow > 0 && (
                      <>
                        <p style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '6px 4px 4px', marginTop: 4 }}>Low ({lowItems.length})</p>
                        {lowItems.slice(0, lowToShow).map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, border: '1px solid #fffbeb', cursor: 'default' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ color: '#334155', fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatProductName(p)}</p>
                              {p.currentStock !== undefined && <p style={{ color: '#94a3b8', fontSize: 11 }}>{formatStock(p.currentStock)} units</p>}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {remaining > 0 && (
                      <button onClick={() => navigate('/new/dashboard/reorder')}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', marginTop: 6, padding: '9px 12px', borderRadius: 10, border: '1px dashed #e2e8f0', background: '#f8fafc', cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 600, transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.color = '#065f46'; e.currentTarget.style.background = '#f0fdf4'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#f8fafc'; }}>
                        <svg style={{ width: 13, height: 13 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                        {remaining} more item{remaining !== 1 ? 's' : ''} — view all →
                      </button>
                    )}
                  </>
                );
              })()}
          </div>

          <button className="v2-btn-reorder" style={{ marginTop: 16 }} onClick={() => navigate('/new/dashboard/reorder')}>
            Generate Reorder List →
          </button>
        </div>
      </div>

      {/* ── Product Performance — table on desktop, cards on mobile ── */}
      <div className="v2-card" style={{ overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f1' }}>
          <div>
            <h3 style={{ color: '#1e293b', fontWeight: 700, fontSize: 15, margin: 0 }}>Product Performance</h3>
            <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Top products by weekly profit</p>
          </div>
          <button onClick={() => navigate('/new/dashboard/products/view')} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#10b981', fontSize: 12, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>
            View all
            <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>

        {topProducts.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14, padding: 32, margin: 0 }}>No product data available</p>

        ) : isDesktop ? (
          /* ── Desktop: table ── */
          <table className="v2-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f1' }}>
                {['Product', 'Stock', 'Profit / Unit', 'Status'].map(h => (
                  <th key={h} style={{ padding: '12px 24px', textAlign: h === 'Product' ? 'left' : 'right', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topProducts.map((p, i) => {
                const stock = Number(p.currentStock);
                const isCritical = stock <= 2;
                const isLow = stock <= 10 && !isCritical;
                return (
                  <tr key={p.productId} style={{ borderBottom: i < topProducts.length - 1 ? '1px solid #f8faf8' : 'none' }}>
                    <td style={{ padding: '14px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: GRADIENT_COLORS[i % GRADIENT_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>{getInitials(p.productName)}</span>
                        </div>
                        <p style={{ color: '#1e293b', fontWeight: 600, fontSize: 13, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                          {formatProductName(p)}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '14px 24px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#475569' }}>{formatStock(p.currentStock)}</td>
                    <td style={{ padding: '14px 24px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#10b981' }}>
                      {p.profit != null ? `K ${Number(p.profit).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '14px 24px', textAlign: 'right' }}>
                      {isCritical ? (
                        <span className="v2-badge" style={{ background: '#fef2f2', color: '#b91c1c' }}>
                          <span className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> Critical
                        </span>
                      ) : isLow ? (
                        <span className="v2-badge" style={{ background: '#fffbeb', color: '#92400e' }}>
                          <span className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} /> Low
                        </span>
                      ) : (
                        <span className="v2-badge" style={{ background: '#f0fdf4', color: '#15803d' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} /> OK
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

        ) : (
          /* ── Mobile: card grid ── */
          <div className="v2-product-grid" style={{ padding: 16 }}>
            {topProducts.map((p, i) => {
              const stock = Number(p.currentStock);
              const isCritical = stock <= 2;
              const isLow = stock <= 10 && !isCritical;
              const statusBadge = isCritical
                ? { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Critical', pulse: true }
                : isLow
                ? { bg: '#fffbeb', color: '#92400e', dot: '#fbbf24', label: 'Low', pulse: true }
                : { bg: '#f0fdf4', color: '#15803d', dot: '#10b981', label: 'OK', pulse: false };
              return (
                <div key={p.productId} style={{ padding: 16, borderRadius: 12, border: '1px solid #f1f5f1', background: '#fafbfa', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: GRADIENT_COLORS[i % GRADIENT_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: 'white', fontSize: 12, fontWeight: 900 }}>{getInitials(p.productName)}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#1e293b', fontWeight: 700, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
                        {formatProductName(p)}
                      </p>
                      <span className="v2-badge" style={{ background: statusBadge.bg, color: statusBadge.color, marginTop: 6, display: 'inline-flex' }}>
                        <span className={statusBadge.pulse ? 'v2-pulse' : ''} style={{ width: 6, height: 6, borderRadius: '50%', background: statusBadge.dot, display: 'inline-block' }} />
                        {statusBadge.label}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', borderTop: '1px solid #f1f5f1', paddingTop: 12 }}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 4px' }}>Stock</p>
                      <p style={{ fontSize: 16, fontWeight: 800, color: '#334155', margin: 0 }}>{formatStock(p.currentStock)}</p>
                    </div>
                    <div style={{ width: 1, background: '#f1f5f1' }} />
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 4px' }}>Profit / Unit</p>
                      <p style={{ fontSize: 16, fontWeight: 800, color: '#10b981', margin: 0 }}>
                        {p.profit != null ? `K ${Number(p.profit).toFixed(2)}` : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ height: 8 }} />
    </div>
  );
}
